import { FunctionComponent, memo, useContext } from "react";
import { IProps } from "./delete-tag.types";
import { EllipsisMenuContext, EllipsisMenuItem } from "@edgetier/client-components";
import { faTrash, faTrashAlt } from "@fortawesome/pro-solid-svg-icons";
import Modal, { ModalContent, ModalFooter, ModalHeader, useModal } from "@edgetier/modal";
import { Button } from "@edgetier/components";
import { axios } from "utilities/axios";
import { ITag, Route } from "@edgetier/watchtower-types";
import { useQueryClient } from "react-query";
import toast from "react-hot-toast";
import { getServerError } from "utilities/get-server-error";
import { useHistory, useLocation } from "react-router-dom";
import urljoin from "url-join";
import TagsListContext from "pages/tag-phrase-triggers/tags-list/tags-list-context/tags-list-context";
import "./delete-tag.scss";

/**
 * A button that, when clicked, opens a modal for deleting a tag.
 * @param props.tag   The name of the tag.
 * @param props.tagId The ID of the tag.
 */
const DeleteTag: FunctionComponent<IProps> = ({ tag, tagId }) => {
    const queryClient = useQueryClient();
    const history = useHistory();
    const location = useLocation();
    const { lockOpen, unlock } = useContext(EllipsisMenuContext);
    const { redirectRoute } = useContext(TagsListContext);
    const { closeModal, openModal, modalProps } = useModal({ onCloseModal: unlock });

    /**
     * Do a DELETE request to the backend to delete the tag.
     */
    const deleteTag = async () => {
        try {
            await axios.delete(Route.Tag.replace(":tagId", tagId));
            queryClient.setQueriesData<ITag[]>(Route.Tags, (tags) => (tags ?? []).filter((tag) => tag.tagId !== tagId));
            toast.success(`Successfully deleted "${tag}"`);

            // If you're deleting the currently open tag, navigate to the default tag.
            if (location.pathname.includes(tagId)) {
                history.push(urljoin(redirectRoute));
            }

            closeModal();
        } catch (serverError) {
            toast.error(getServerError(serverError));
        }
    };

    return (
        <>
            <EllipsisMenuItem
                closeAfterClick={false}
                icon={faTrash}
                onClick={() => {
                    lockOpen();
                    openModal();
                }}
            >
                Delete
            </EllipsisMenuItem>
            <Modal {...modalProps}>
                <ModalHeader>
                    <h2>Delete Tag</h2>
                </ModalHeader>
                <ModalContent>
                    <p>
                        Are you sure you want to remove the "{tag}" tag? This will remove the tag for all users and
                        cannot be undone.
                    </p>
                </ModalContent>
                <ModalFooter>
                    <Button icon={faTrashAlt} onClick={deleteTag} styleName="negative">
                        Yes, delete tag
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default memo(DeleteTag);
